import React, { useEffect, useState } from 'react';
import { Stack } from '@mui/material';
import { LiveSearchBox, LiveSearchListItem } from '../../atoms/LiveSearchBox';
import { PartTypeActionSearchItem } from '../PartTypeActionSearchItem';
import { PartTypeActionSearchProps } from './PartTypeActionSearch.props';
import { LiveSearchBoxWrapper } from './PartTypeActionSearch.styles';
import { useSearchPartsOrPartTypes } from '../../../queries/live-search-query';
import { ActionPermissions, CustomPermission, PartTypeActionLiveSearchItem, PartTypeActionLiveSearchItemCategoryCode } from '../../../@types';
import { Action, PartActionType } from '../../../@types/partAction.type';
import { PartLocationType } from '../../../@types/part.type';
import { isUserHasPermission } from '../../../configs/permissions';
import { useGetUserSystemPermissions } from '../../../queries/user-query';
import { CREATE_SERIALISED_POPUP_ACTIONS } from '../../../constants/partAction';

export const PartTypeActionSearch: React.FC<PartTypeActionSearchProps> = ({
  title,
  serialiseType = '',
  serviceData,
  partTypeId = '',
  value,
  locationId,
  partActionType,
  error,
  helperText,
  action,
  searchType,
  IsPartCreationEnabled,
  onChange,
  outPartTypeId,
  serialisedOutPartId,
  nsOutPartId,
  preLoadPartTypes
}) => {
  const [permissions, setPermissions] = useState<CustomPermission[]>();
  const [defaultSuggestions, setDefaultSuggestions] = useState<any[]>([]);
  const [searchedText, setSearchedText] = useState<string>('');

  const getUserPermissionsQuery = useGetUserSystemPermissions();
  const searchPartsOrPartTypesQuery = useSearchPartsOrPartTypes();

  useEffect(() => {
    getUserPermissionsQuery.data && setPermissions(getUserPermissionsQuery.data);
  }, [getUserPermissionsQuery.data]);

  // Get Repair and Test part actions part suggestions
  useEffect(() => {
    if ([PartActionType.REPAIR, PartActionType.TEST].includes(partActionType!) && serviceData?.reportedSerialNumberPartId) {
      const getDefaultSuggestions = async() => {
        const results = await searchPartsOrPartTypesQuery.mutateAsync({
          name: '',
          serialiseType,
          partTypeId,
          clientContractIds: [serviceData?.clientId || 0, serviceData?.contractId || 0],
          locationId: locationId ? locationId : '',
          allowedPartLocationType: '',
          partActionType,
          searchType,
          reportedSerialNumberPartId: serviceData?.reportedSerialNumberPartId
        });
        const defaultOptions = results?.map((result) => ({
          ...result,
          serial1: result?.serial1,
          name: result?.partTypeName
        }));
        setDefaultSuggestions(defaultOptions);
      };
      getDefaultSuggestions();
    }
  }, [serviceData?.reportedSerialNumberPartId]);

  useEffect(() => {
    if (preLoadPartTypes) {
      if (partActionType && CREATE_SERIALISED_POPUP_ACTIONS.includes(partActionType) && !searchedText) {
        const getDefaultSuggestions = async() => {
          const results = await searchPartsOrPartTypesQuery.mutateAsync({
            name: '',
            serialiseType,
            partTypeId,
            clientContractIds: [serviceData?.clientId || 0, serviceData?.contractId || 0],
            locationId: locationId ? locationId : '',
            allowedPartLocationType: partActionType === PartActionType.INSTALL || action === Action.IN ? PartLocationType.Node : '',
            partActionType,
            searchType,
            preFill: true
          });

          const defaultOptions = results?.map((result) => ({
            ...result,
            serial1: result?.serial1,
            name: result?.partTypeName
          }));
          setDefaultSuggestions(defaultOptions);
        };
        getDefaultSuggestions();
      } else if (partActionType && CREATE_SERIALISED_POPUP_ACTIONS.includes(partActionType) && searchedText) {
        setDefaultSuggestions([]);
      }
    }
  }, [preLoadPartTypes]);

  // Get replace OUT part suggestions
  useEffect(() => {
    if (partActionType === PartActionType.REPLACE && action === Action.OUT && !value) {
      const getDefaultSuggestions = async() => {
        const results = await searchPartsOrPartTypesQuery.mutateAsync({
          name: '',
          serialiseType,
          partTypeId,
          clientContractIds: [serviceData?.clientId || 0, serviceData?.contractId || 0],
          locationId: locationId ? locationId : '',
          allowedPartLocationType: '',
          partActionType,
          searchType,
          serviceTypeCode: serviceData?.serviceTypeCode,
          ...(serviceData?.reportedSerialNumberPartId && { reportedSerialNumberPartId: serviceData?.reportedSerialNumberPartId })
        });
        const defaultOptions = results?.map((result) => ({
          ...result,
          serial1: result?.serial1,
          name: result?.partTypeName
        }));
        if (!preLoadPartTypes && isUserHasPermission(ActionPermissions.Service_Edit_Part_Actions_Exceptional_Creation, permissions)) {
          defaultOptions.push({
            name: '',
            partTypeId: 0, 
            partTypeName: '', 
            categoryCode: PartTypeActionLiveSearchItemCategoryCode.Serialised, 
            conditionCode: '',
            serial1: '', 
            softwareVersion: '', 
            hardwareVersion: '', 
            firmwareVersion: '',
            fleetTagName: '', 
            locationName: '', 
            locationId: 0,
            serviceId: 0,
            isPartCreation: true
          });
        }
        setDefaultSuggestions(defaultOptions);
      };
      getDefaultSuggestions();
    }
  }, [permissions]);

  // Get replace IN part suggestions
  useEffect(() => {
    if (partActionType === PartActionType.REPLACE && action === Action.IN && outPartTypeId && (serialisedOutPartId || nsOutPartId)) {
      const getDefaultSuggestions = async() => {
        const results = await searchPartsOrPartTypesQuery.mutateAsync({
          name: '',
          serialiseType,
          partTypeId,
          clientContractIds: [serviceData?.clientId || 0, serviceData?.contractId || 0],
          locationId: locationId ? locationId : '',
          allowedPartLocationType: PartLocationType.Node,
          partActionType,
          searchType,
          outPartTypeId,
          ...(serialisedOutPartId && { serialisedOutPartId: serialisedOutPartId }),
          ...(nsOutPartId && { nsOutPartId: nsOutPartId })
        });
        const defaultOptions = results?.map((result) => ({
          ...result,
          serial1: result?.serial1,
          name: result?.partTypeName
        }));
        setDefaultSuggestions(defaultOptions);
      };
      getDefaultSuggestions();
    }
  }, [serialisedOutPartId, nsOutPartId]);

  return (
    <Stack direction="row" alignItems="center" spacing={1} width="100%">
      <LiveSearchBoxWrapper>
        <LiveSearchBox
          defaultSuggetions={defaultSuggestions}
          title={title}
          timeOffset={400} 
          value={value}
          isError={error}
          helperText={helperText}
          onClearValue={() => {
            setSearchedText('');
          }}
          feature="part-action"
          onChange={(val) => {
            if (partActionType === PartActionType.REPLACE && action === Action.OUT && !value && !val && !defaultSuggestions?.some((suggestion) => suggestion?.isPartCreation)) {
              const updatedSuggestions = [...defaultSuggestions];
              updatedSuggestions.push({
                name: '',
                partTypeId: 0, 
                partTypeName: '', 
                categoryCode: PartTypeActionLiveSearchItemCategoryCode.Serialised, 
                conditionCode: '',
                serial1: '', 
                softwareVersion: '', 
                hardwareVersion: '', 
                firmwareVersion: '',
                fleetTagName: '', 
                locationName: '', 
                locationId: 0,
                serviceId: 0,
                isPartCreation: true
              });
              setDefaultSuggestions(updatedSuggestions);
            }
            onChange(val);
          }}
          renderItem={(props: any, option: PartTypeActionLiveSearchItem) => { 
            if (option?.isPartCreation && isUserHasPermission(ActionPermissions.Service_Edit_Part_Actions_Exceptional_Creation, permissions)){
              return (
                <LiveSearchListItem {...props}>
                  <PartTypeActionSearchItem
                    name={option.name}
                    serial={''}
                    partTypeId={0}
                    partId={''}
                    nsPartId={''}
                    category={''}
                    isCreateAction={true}
                  />
                </LiveSearchListItem>
              );
            }

            return (
              <LiveSearchListItem {...props}>
                <PartTypeActionSearchItem 
                  name={option.partTypeName}
                  serial={option.serial1}
                  partTypeId={option.partTypeId}
                  partId={option?.partId?.toString() || ''}
                  nsPartId={option?.ns_partId?.toString() || ''}
                  category={option.categoryCode}
                />
              </LiveSearchListItem>
            );
          }}
          onApiInvoke={async (name) => {
            if (preLoadPartTypes) {
              const matchingPartTypes = defaultSuggestions.filter((suggestion) =>
                suggestion.name?.toLowerCase()?.includes(name?.toLowerCase()) ||
                suggestion.partTypeName?.toLowerCase()?.includes(name?.toLowerCase())
              ) || [];
              return matchingPartTypes;
            };
            setSearchedText(name);
            let allowedPartLocationType = '';
            const clientContractIds: Array<number> = [serviceData?.clientId || 0, serviceData?.contractId || 0];
            if (partActionType === PartActionType.REPLACE || partActionType === PartActionType.REMOVE || partActionType === PartActionType.DOA) {
              serialiseType = PartTypeActionLiveSearchItemCategoryCode.Serialised;
            }

            if (partActionType === PartActionType.INSTALL || action === Action.IN) {
              allowedPartLocationType = PartLocationType.Node;
            }

            const inputValue = name.replace('#', '%23');
            const results = await searchPartsOrPartTypesQuery.mutateAsync({
              name: inputValue,
              serialiseType,
              partTypeId,
              clientContractIds: clientContractIds,
              locationId: locationId ? locationId : '',
              allowedPartLocationType,
              partActionType,
              searchType
            });

            let options: PartTypeActionLiveSearchItem[] = [];

            if (!preLoadPartTypes && IsPartCreationEnabled && isUserHasPermission(ActionPermissions.Service_Edit_Part_Actions_Exceptional_Creation, permissions) && results.length == 0) {
              if (defaultSuggestions?.some((suggestion) => suggestion?.isPartCreation)) {
                const filteredDefaultSuggestions = defaultSuggestions?.filter((suggestion) => !suggestion?.isPartCreation);
                setDefaultSuggestions(filteredDefaultSuggestions);
              }
              options.push({
                name: inputValue,
                partTypeId: 0, 
                partTypeName: '', 
                categoryCode: PartTypeActionLiveSearchItemCategoryCode.Serialised, 
                conditionCode: '',
                serial1: '', 
                softwareVersion: '', 
                hardwareVersion: '', 
                firmwareVersion: '',
                fleetTagName: '', 
                locationName: '', 
                locationId: 0,
                serviceId: 0,
                isPartCreation: true
              });
            }

            if (searchType === 'part') {
              options = results?.map((result) => ({
                ...result,
                serial1: result?.serial1,
                name: result.partTypeName
              })) || [];
  
              return options;
            } else if (searchType === 'part-type') {
              options = results?.map((result) => ({
                ...result,
                name: result.partTypeName
              })) || [];
            } else {
              results?.map((result) => {
                if (result.serial1) {
                  options.push({ ...result, serial1: result.serial1, name: '' });
                } else {
                  options.push({ ...result, name: result.partTypeName });
                }
              });
            }

            return options;
          }}
          byPassDefaultSuggestions={[PartActionType.REPAIR, PartActionType.TEST].includes(partActionType!) || preLoadPartTypes}
        />
      </LiveSearchBoxWrapper>
    </Stack>
  );
};